<template>
   <div class="users-container">
        <div class="content">
            <div class="search-form">
                <input
                    class="search-input"
                    name="search"
                    placeholder="Find a player"
                    v-model="search.username"
                    @input="onSearch(search.username)"
                    autocomplete="off"
                />
                <span class="search-icon">
                    <fa-icon icon="search" />
                </span>
            </div>
            <div class="user-list-wrapper">
                <div class="content placeholder" v-if="!loaded">
                    <div class="user-list-placeholder">
                        Loading users<span class="ellipsis animated-ellipsis"></span>
                    </div>
                </div>
                <div class="user-list" @scroll="onScroll">
                    <div class="user-result-wrapper" v-for="user in users" :key="`user-${user.id}`">
                        <router-link :to="`/matches/${encodeLabel(user.username)}`" class="link" target="_blank">
                            <div class="user-result">
                                <span class="username">{{ user.username }}</span>
                                <rank :skillRating="user.skillRating" />
                            </div>
                        </router-link>
                    </div>
                    <div v-if="loaded && !users.length">
                        Your search did not match any players
                    </div>
                    <div class="load-more" ref="load-more" v-if="!loading">
                    </div>
                    <div class="load-more" ref="load-more" v-if="loading">
                        <spinner
                            class="spinner-wrapper"
                            size="medium"
                            line-fg-color="#776d6d"
                            :speed="1.2"
                        />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import debounce from 'lodash.debounce';
import throttle from 'lodash.throttle';

export default {
    name: 'Users',
    data() {
        return {
            search: {
                username: '',
            },
            loaded: false,
            loading: true,
            page: 1,
        };
    },
    async created() {
        if (!this.isSignedIn) {
            this.$router.push('/login');
        }

        await this.$store.dispatch('getUsers');
        this.loaded = true;
        this.loading = false;

        if (this.bottomIsVisible && this.shouldLoadMore()) {
            await this.loadMore();
        }
    },
    watch: {
        isSignedIn(current, prev) {
            if (prev && !current) {
                this.$router.push('/login');
            }
        },
    },
    computed: {
        usersState() {
            return this.$store.state.usersState;
        },
        pages() {
            return this.usersState.pages;
        },
        pageSize() {
            return this.usersState.pageSize;
        },
        users() {
            return this.usersState.users;
        },
        loadMoreRef() {
            return this.$refs['load-more'];
        },
        isSignedIn() {
            return this.$store.state.loginState.userId;
        },
    },
    methods: {
        onSearch: debounce(async function onSearch(username) {
            const page = 1;
            this.page = page;
            this.loaded = false;
            this.loading = true;
            this.$store.dispatch('getUsers', { username, page });
            this.loaded = true;
            this.loading = false;
        }, 550),
        loadMore() {
            if (this.loading) {
                return;
            }
            this.loading = true;
            const nextPage = this.page + 1;
            const username = this.search.username;
            this.$store.dispatch('getUsers', { username, page: nextPage });
            this.page = nextPage;
            this.loading = false;
        },
        shouldLoadMore() {
            return this.users.length <
                (this.pages * this.pageSize) &&
                this.page < this.pages &&
                !this.loading;
        },
        onScroll: throttle(async function onScroll() {
            if (this.bottomIsVisible() && this.shouldLoadMore()) {
                await this.loadMore();
            }
        }, 300),
        bottomIsVisible() {
            const rect = this.loadMoreRef.getBoundingClientRect();

            return rect.bottom > 0 &&
                rect.right > 0 &&
                rect.left < (window.innerWidth || document.documentElement.clientWidth) &&
                rect.top < (window.innerHeight || document.documentElement.clientHeight);
        },
        encodeLabel(label) {
            return encodeURIComponent(label);
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .users-container {
        text-align: center;
        display: flex;
        flex-direction: column;
        flex-grow: 1;

        .content {
            display: flex;
            flex-direction: column;
            flex-grow: 1;
            padding: 25px 25px 10px 25px;

            &.placeholder {
                display: flex;
                flex-direction: column;
                justify-content: center;
            }

            .search-form {
                margin-bottom: 25px;
                position: relative;
                width: calc(100% - 25px);

                .search-input {
                    width: 100%;
                    background: transparent;
                    border: 0;
                    border-bottom: 1px solid $dark-blue;
                    font-size: 1.1em;
                    padding-bottom: 5px;
                    padding-left: 25px;

                    &::placeholder {
                        color: $dark-blue;
                    }
                }

                .search-icon {
                    position: absolute;
                    top: 0;
                    left: 0;
                }
            }

            .user-list-wrapper {
                flex-grow: 1;
                display: flex;
                flex-direction: column;

                .user-list {
                    display: flex;
                    position: relative;
                    flex-wrap: wrap;
                    padding: 10px;
                    justify-content: center;
                    height: 0;
                    flex-grow: 1;
                    overflow-y: scroll;

                    .user-result-wrapper {
                        flex: 1 1 45%;
                        margin: 5px;

                        .user-result {
                            background: white;
                            display: flex;
                            justify-content: center;
                            padding: 14px;
                            box-sizing: border-box;
                            border-radius: 4px;
                            border: 1px solid $sage-brown;

                            .username {
                                vertical-align: middle;
                            }
                        }

                        &:hover {

                            .user-result {
                                border-color: $gold-alt;
                            }
                        }

                        .link {
                            white-space: nowrap;
                            color: $dark-blue;
                        }
                    }
                }
            }
        }

        .load-more {
            height: 75px;
            width: 100%;
        }

        .spinner-wrapper {
            display: inline-block;
            margin-top: 10px;
        }
    }

    @media only screen and (min-width: $screen-sm) {

        .users-container {

            .content {

                .user-list-wrapper {
                    .user-list {
                        padding: 10px 20px;

                        .user-result-wrapper {
                            flex: 1 1 20%;
                            margin: 10px;
                            max-width: 212px;
                        }
                    }
                }
            }
        }
    }

</style>
